import { useId } from "react";
import { ChevronDown, Switch } from "@safers/ui/atoms";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@safers/ui/molecules";
import type { AerialPhotoCatalog } from "./aerialPhotos";

interface AerialPhotoControlProps {
  catalog: AerialPhotoCatalog;
  dates: readonly string[];
  selectedDate: string;
  enabled: boolean;
  onDateChange: (date: string) => void;
  onEnabledChange: (enabled: boolean) => void;
}

export function AerialPhotoControl({
  catalog,
  dates,
  selectedDate,
  enabled,
  onDateChange,
  onEnabledChange,
}: AerialPhotoControlProps) {
  const switchId = useId();
  const disabled = dates.length === 0 || !!catalog.isLoading || !!catalog.isError;
  const dateDisabled = disabled || !enabled;
  const dateLabel = catalog.isLoading
    ? "불러오는 중…"
    : catalog.isError
      ? "불러오기 실패"
      : selectedDate
        ? selectedDate.replaceAll("-", ".")
        : "촬영 기록 없음";
  const dateButton = (
    <button
      type="button"
      aria-label={`항공사진 촬영 날짜: ${selectedDate || "없음"}`}
      disabled={dateDisabled}
      className="group flex h-8 w-36 items-center justify-between gap-2 rounded-md px-2 text-xs font-medium tabular-nums text-neutral-800 transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:bg-neutral-100"
    >
      <span>{dateLabel}</span>
      <ChevronDown
        size="sm"
        aria-hidden="true"
        className="text-neutral-400 motion-safe:transition-transform group-data-[state=open]:rotate-180"
      />
    </button>
  );
  return (
    <fieldset
      aria-label="항공사진 설정"
      aria-busy={catalog.isLoading}
      className="absolute right-3 top-3 z-[500] max-w-[calc(100%-1.5rem)] rounded-lg border border-neutral-200 bg-white/95 p-1 text-xs text-neutral-700 shadow-sm"
      onPointerDown={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onWheel={(event) => event.stopPropagation()}
    >
      <div className="flex items-center">
        <div className="mr-1 flex h-5 shrink-0 items-center gap-2 border-r border-neutral-200 px-2 pr-3">
          <label
            htmlFor={switchId}
            className="flex h-8 cursor-pointer items-center font-medium text-neutral-800"
          >
            항공사진
          </label>
          <Switch
            id={switchId}
            checked={enabled}
            disabled={disabled}
            onCheckedChange={onEnabledChange}
            className="h-5 w-8 [&>span]:h-4 [&>span]:w-4 [&>span[data-state=checked]]:translate-x-3"
          />
        </div>
        {dateDisabled ? (
          dateButton
        ) : (
          <DropdownMenu key={catalog.siteId} modal={false}>
            <DropdownMenuTrigger asChild>{dateButton}</DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={5}
              collisionPadding={12}
              className="z-[1000] flex max-h-[var(--radix-dropdown-menu-content-available-height)] w-[var(--radix-dropdown-menu-trigger-width)] min-w-0 flex-col rounded-lg p-1"
            >
              <DropdownMenuRadioGroup
                aria-label="항공사진 촬영 날짜"
                value={selectedDate}
                onValueChange={onDateChange}
                className="min-h-0 max-h-48 overflow-y-auto overscroll-contain"
              >
                {dates.map((date, index) => (
                  <DropdownMenuRadioItem
                    key={date}
                    value={date}
                    className="min-h-8 cursor-pointer gap-1 rounded-md pl-6 pr-1 text-xs tabular-nums data-[state=checked]:bg-brand/10 data-[state=checked]:font-semibold data-[state=checked]:text-brand-active"
                  >
                    <span>{date.replaceAll("-", ".")}</span>
                    {index === 0 && (
                      <span className="ml-auto text-[0.625rem] font-normal text-neutral-500">
                        최신
                      </span>
                    )}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {catalog.isError && catalog.onRetry && (
        <div role="alert" className="px-2 py-1">
          <button type="button" className="underline underline-offset-2" onClick={catalog.onRetry}>
            다시 시도
          </button>
        </div>
      )}
    </fieldset>
  );
}
